import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, AlertTriangle, Check, User, RefreshCw } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert markdown bold **text** to bolded JSX
function renderInline(text) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="text-white font-semibold">{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function CompliancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("pink_token") : null;
  const isAdmin = (() => {
    try {
      if (!token) return false;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.is_admin === true;
    } catch { return false; }
  })();

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/compliance/daily`, { headers });
      setData(res.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setError("disabled");
      else if (status === 401) setError("login");
      else setError("generic");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const refresh = async () => {
    if (!isAdmin) return;
    setRefreshing(true);
    try {
      await axios.post(`${API}/admin/compliance/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Compliance data refreshed");
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-[calc(6rem+env(safe-area-inset-top))] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error === "disabled") {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-[calc(6rem+env(safe-area-inset-top))] max-w-3xl mx-auto px-6 py-16 text-center">
          <h1 className="text-2xl font-bold text-white mb-3">Daily Ink Compliance</h1>
          <p className="text-neutral-500">Această pagină nu este disponibilă momentan.</p>
        </div>
      </div>
    );
  }

  if (error === "login") {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-[calc(6rem+env(safe-area-inset-top))] max-w-3xl mx-auto px-6 py-16 text-center">
          <h1 className="text-2xl font-bold text-white mb-3">Daily Ink Compliance</h1>
          <p className="text-neutral-400">Please log in to view the daily compliance briefing.</p>
          <a href="/login" className="inline-block mt-6 px-5 py-2 bg-purple-600 hover:bg-purple-500 rounded-lg text-white font-semibold text-sm transition-colors" data-testid="compliance-login-cta">
            Log in
          </a>
        </div>
      </div>
    );
  }

  const c = data?.content;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="compliance-page">
      <Navbar />
      <div className="pt-[calc(6rem+env(safe-area-inset-top))] max-w-6xl mx-auto px-4 sm:px-6 pb-24">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-purple-400 mb-2">Daily Briefing</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
              Daily Ink <span className="text-purple-400">Compliance</span>
            </h1>
            <p className="text-neutral-500 mt-2 text-sm">
              Reguli, legislație și noutăți pentru salon.
              {data?.date && <span className="ml-2 opacity-70">({data.date})</span>}
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={refresh}
              disabled={refreshing}
              className="self-start sm:self-auto inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-xs text-neutral-300 font-semibold uppercase tracking-wider transition-colors disabled:opacity-50"
              data-testid="compliance-refresh"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          )}
        </div>

        {!c && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6 text-amber-300 text-sm">
            Nu am putut extrage briefingul de azi. Un admin poate încerca <em>Refresh</em>.
          </div>
        )}

        {c && (
          <>
            {/* LEGISLAȚIE section — top */}
            <section className="rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-950/40 via-black/40 to-black/60 p-6 sm:p-8 mb-8" data-testid="compliance-legislation">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-purple-500/15 border border-purple-500/30">
                  <Shield className="w-5 h-5 text-purple-300" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-purple-400">Rubrica 1</p>
                  <h2 className="text-xl font-bold text-white">Legislație</h2>
                </div>
              </div>
              {c.legislation_intro && (
                <p className="text-neutral-300 leading-relaxed mb-5 text-sm sm:text-base">
                  {renderInline(c.legislation_intro)}
                </p>
              )}
              {c.legislation_items?.length > 0 && (
                <ul className="space-y-3">
                  {c.legislation_items.map((item, i) => (
                    <li key={i} className="flex gap-3 items-start text-sm sm:text-base leading-relaxed text-neutral-300">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
                      <span>{renderInline(item)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* ARTIST DO / DON'T (grid) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
              <ComplianceCard
                variant="do" audience="Artist"
                icon={<User className="w-5 h-5 text-emerald-300" />}
                text={c.do_artist}
                testId="compliance-do-artist"
              />
              <ComplianceCard
                variant="dont" audience="Artist"
                icon={<User className="w-5 h-5 text-rose-300" />}
                text={c.dont_artist}
                testId="compliance-dont-artist"
              />
            </div>

            {/* CLIENT DO / DON'T (grid) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <ComplianceCard
                variant="do" audience="Client"
                icon={<User className="w-5 h-5 text-emerald-300" />}
                text={c.do_client}
                testId="compliance-do-client"
              />
              <ComplianceCard
                variant="dont" audience="Client"
                icon={<User className="w-5 h-5 text-rose-300" />}
                text={c.dont_client}
                testId="compliance-dont-client"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ComplianceCard({ variant, audience, icon, text, testId }) {
  const isDo = variant === "do";
  return (
    <div
      className={`rounded-2xl p-6 border ${
        isDo
          ? "border-emerald-500/20 bg-gradient-to-br from-emerald-950/30 to-black/40"
          : "border-rose-500/20 bg-gradient-to-br from-rose-950/30 to-black/40"
      }`}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 flex items-center justify-center rounded-lg border ${
          isDo ? "bg-emerald-500/10 border-emerald-500/30" : "bg-rose-500/10 border-rose-500/30"
        }`}>
          {isDo ? <Check className="w-5 h-5 text-emerald-300" /> : <AlertTriangle className="w-5 h-5 text-rose-300" />}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            {audience}
          </p>
          <h3 className={`text-lg font-bold ${isDo ? "text-emerald-300" : "text-rose-300"}`}>
            {isDo ? "DO" : "DON'T"}
          </h3>
        </div>
      </div>
      <p className="text-neutral-300 text-sm sm:text-base leading-relaxed">
        {text ? renderInline(text) : <em className="text-neutral-500">—</em>}
      </p>
    </div>
  );
}
